import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { isVendorPlaceholderPhotoUrl } from '@shared/utils/accommodations/vendorPlaceholderPhotos';
import { addRendition } from '@shared/utils/photoRendition';
import { stripHtmlTags } from '@shared/utils/sanitizeHtmlContent';

/**
 * Single source of the PDP's Open Graph content derivation (title, image,
 * description, canonical URL). The date label is locale-formatted in
 * `StaysPdpHead`, which has the locale context this module must not need. Consumed by `StaysPdpHead` (the real
 * og: meta tags) and Share Your Stay's `useSmsLandingPreview` (the mocked
 * iMessage unfurl card) — the mock renders whatever the crawlers will see,
 * so the two must not drift. Lives in stays/utils (not beside StaysPdpHead)
 * because it crosses the EventAccommodations/PublicStays boundary.
 */

const MAX_OG_DESCRIPTION_LENGTH = 200;

// Structural subset of `accommodationCatalogPropertyById` — both consumers
// fetch via HotelStaticByJoyPlace, but taking the shape (not the generated
// type) keeps this file free of a .graphql.generated dependency.
export type HotelOgSource = Readonly<{
  name?: string | null;
  address?: Readonly<{ city?: string | null; state?: string | null; country?: string | null }> | null;
  photos?: ReadonlyArray<Readonly<{ url?: string | null }>> | null;
  plainTextLongDescription?: string | null;
}>;

/**
 * Builds a short location string from the hotel address, e.g. "New York, NY" or "Paris, France".
 */
export const buildLocationString = (address: HotelOgSource['address']): string | undefined => {
  if (!address) return undefined;
  const parts = [address.city, address.state ?? address.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
};

/**
 * og:title — "Hotel Name – City, ST" with any extra detail (e.g. a date range)
 * joined by "·": "Hotel Name – City, ST · Jul 17–19, 2026".
 */
export const buildHotelOgTitle = (property: HotelOgSource, extraDetail?: string): string => {
  const hotelName = property.name || 'Hotel';
  const details = [buildLocationString(property.address), extraDetail].filter(Boolean);
  return details.length ? `${hotelName} – ${details.join(' · ')}` : hotelName;
};

/** og:image — the first real catalog photo (medium rendition; vendor "NO IMAGE" placeholders skipped), or the stock hotel image. */
export const buildHotelOgImageUrl = (property: HotelOgSource): string => {
  const firstPhotoUrl = property.photos?.find(photo => photo.url && !isVendorPlaceholderPhotoUrl(photo.url))?.url;
  return firstPhotoUrl ? (addRendition({ url: firstPhotoUrl, renditionSize: 'medium' }) as string) : DEFAULT_HOTEL_IMAGE;
};

/** og:description — the hotel's long description, tags stripped, capped at 200 chars. */
export const buildHotelOgDescription = (property: HotelOgSource): string | undefined => {
  const description = property.plainTextLongDescription ? stripHtmlTags(property.plainTextLongDescription) : undefined;
  if (description && description.length > MAX_OG_DESCRIPTION_LENGTH) {
    return `${description.slice(0, MAX_OG_DESCRIPTION_LENGTH)}…`;
  }
  return description;
};

/**
 * og:url — the canonical URL for a stays PDP, preserving the eventId and date query
 * params so the URL round-trips correctly when crawlers follow it. Including the dates
 * keeps date-specific shares distinct for OG consumers that key the preview on the URL.
 */
export const buildCanonicalUrl = (joyPlaceId: string, eventId: string | null, checkIn: string | null, checkOut: string | null): string => {
  const base = `https://withjoy.com/stays/${joyPlaceId}`;
  const params = new URLSearchParams();
  if (eventId) params.set('eventId', eventId);
  if (checkIn) params.set('checkin', checkIn);
  if (checkOut) params.set('checkout', checkOut);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
};
